import { Injectable } from '@angular/core';
import { HeadleaseDetails } from '../models/headlease-details';
import { Details } from '../../details/Interfaces/details';
import { DataFormatter } from '../../common/services/data-formatter.service';
import { LayoutOrientation } from '../../common/enums/layout-orientation';
import { yOrN } from '@common/string-formatter/string-formatter';
import { DetailsBuilder } from '@details/Interfaces/details-builder';
import Horizontal = LayoutOrientation.Horizontal;
import { nameOnly, simple, DetailsItem } from '@details/Interfaces/details-item-types';

@Injectable()
export class HeadLeaseDetailsMapper {
    public map(details: HeadleaseDetails, dataFormatter: DataFormatter): Details {

        const inBreachText = details.BreachStatus.toLowerCase() !== 'none' ? ' - in breach' : '';

        const b = new DetailsBuilder(`Reference ${details.PropertyRef} / ${details.Ref}${inBreachText}`, details);

        let status = 'current';
        if (details.TerminationStatus.toLowerCase() !== 'current') {
          status = 'non current';
        }

        b.section(`This is a ${status} head lease for:`, LayoutOrientation.Vertical)
          .conditionalItem(details.PropertyName, nameOnly(details.PropertyName))
          .for('PropertyAddress', 'Address:')
            .for('LandlordName', 'Landlord:');

        b.section('Key information:', Horizontal)
          .for('LeaseTerm', 'The head lease term is for:')
          .conditionalItem(details.TermStartDate !== undefined,
            simple('Term starts on:', dataFormatter.formatDate(details.TermStartDate)))
          .conditionalItem(details.TermEndDate !== undefined,
              simple('Term ends on:', dataFormatter.formatDate(details.TermEndDate)));

        b.section('', Horizontal)
            .always(simple('Annual rent is:', details.PassingRent ? dataFormatter.formatNumeric(details.PassingRent) + this.getCurrencyCodeSuffix(details) : 'None'))
            .always(simple('Annual service charge is:', details.AnnualSerivceCharge ? dataFormatter.formatNumeric(details.AnnualSerivceCharge) + this.getCurrencyCodeSuffix(details) : 'None'));

        b.section('Additional information:', Horizontal)
          .always(simple('The next review is due on:',
            details.NextReviewDate ? dataFormatter.formatDate(details.NextReviewDate) : 'None'))
          .always(simple('The next option is due on:',
              details.OptionDate ? dataFormatter.formatDate(details.OptionDate) : 'None'));

        b.section('', Horizontal)
          .conditionalItem(details.AssignmentPermitted,this.getAssignmentPermitted(details.AssignmentPermitted))
          .conditionalItem(details.SublettingPermitted, this.getSublettingPermitted(details.SublettingPermitted))
          .conditionalItem(details.AlterationsPermitted ,this.getAlterationsPermitted(details.AlterationsPermitted))
          .conditionalItem(details.RestrictiveCovenants, this.getRestrictiveCovenants(details.RestrictiveCovenants))
          .conditionalItem(details.ChangeOfUsePermitted, this.getChangeOfUsePermitted(details.ChangeOfUsePermitted));

        return b.getDetails();
    }



  private getAssignmentPermitted(assignmentPermitted: string): DetailsItem {
    const permitted = yOrN(assignmentPermitted, 'can', 'cannot');
    if (!permitted) return undefined;
    return nameOnly(`This head lease ${permitted} be assigned.`);
  }

  private getSublettingPermitted(sublettingPermitted: string): DetailsItem {
    const permitted = yOrN(sublettingPermitted, 'can', 'cannot');
    if (!permitted) return undefined;
    return nameOnly(`This head lease ${permitted} be sublet.`);

  }

  private getAlterationsPermitted(alterationsPermitted: string): DetailsItem {
    const allows = yOrN(alterationsPermitted, 'does', 'does not');
    if (!allows) return undefined;
    return nameOnly(`This head lease ${allows} allow alterations.`);
  }

  private getRestrictiveCovenants(restrictiveCovenants: string): DetailsItem {
    const are = yOrN(restrictiveCovenants, 'are', 'are no');
    if (!are) return undefined;
    return nameOnly(`There ${are} restrictive covenants.`);
  }

  private getChangeOfUsePermitted(changeOfUsePermitted: string): DetailsItem {
    const permitted = yOrN(changeOfUsePermitted, 'is', 'is not');
    if (!permitted) return undefined;
    return nameOnly(`Change of use ${permitted} permitted.`);
  }

  private getCurrencyCodeSuffix(headleaseDetails: HeadleaseDetails): string {
    return headleaseDetails.DefaultCurrencyCode ? ' ' + headleaseDetails.DefaultCurrencyCode : '';
  }
}
